import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge } from '@/components/StatusBadge';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function Usuarios() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [nombre, setNombre] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [rol, setRol] = useState('consulta');
  const [saving, setSaving] = useState(false);

  const fetchUsers = async () => {
    try { const res = await API.get('/auth/users'); setUsers(res.data); } catch { toast.error('Error'); }
  };
  useEffect(() => { fetchUsers(); }, []);

  const handleSave = async () => {
    if (!nombre || !username) { toast.error('Nombre y usuario son requeridos'); return; }
    if (!editing && !password) { toast.error('Contraseña requerida para nuevo usuario'); return; }
    setSaving(true);
    try {
      if (editing) {
        const data = { nombre, email, rol };
        if (password) data.password = password;
        await API.put(`/auth/users/${editing.id}`, data);
        toast.success('Usuario actualizado');
      } else {
        await API.post('/auth/users', { nombre, username, password, email, rol });
        toast.success('Usuario creado');
      }
      setOpen(false); setEditing(null); resetForm();
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar usuario?')) return;
    try { await API.delete(`/auth/users/${id}`); toast.success('Eliminado'); fetchUsers(); } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const resetForm = () => { setNombre(''); setUsername(''); setPassword(''); setEmail(''); setRol('consulta'); };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Usuarios</h1>
          <p className="text-sm text-muted-foreground">Gestión de usuarios y roles</p>
        </div>
        <Button onClick={() => { setEditing(null); resetForm(); setOpen(true); }} className="bg-[#0F6E87] hover:bg-[#0C5D73]">
          <Plus className="h-4 w-4 mr-1" /> Nuevo
        </Button>
      </div>

      <Card className="bg-white border"><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow className="bg-muted/30">
            <TableHead>Nombre</TableHead><TableHead>Usuario</TableHead><TableHead>Email</TableHead>
            <TableHead>Rol</TableHead><TableHead>Estado</TableHead><TableHead className="text-right">Acciones</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {users.map(u => (
              <TableRow key={u.id} className="hover:bg-muted/40">
                <TableCell className="font-medium">{u.nombre}</TableCell>
                <TableCell>{u.username}</TableCell>
                <TableCell className="text-muted-foreground">{u.email}</TableCell>
                <TableCell><StatusBadge status={u.rol} /></TableCell>
                <TableCell>{u.activo !== false ? <span className="text-emerald-600 text-xs">Activo</span> : <span className="text-red-600 text-xs">Inactivo</span>}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditing(u); setNombre(u.nombre); setUsername(u.username); setEmail(u.email || ''); setRol(u.rol); setPassword(''); setOpen(true); }}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete(u.id)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? 'Editar' : 'Nuevo'} Usuario</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-sm font-medium">Nombre *</label><Input value={nombre} onChange={(e) => setNombre(e.target.value)} className="mt-1" /></div>
            {!editing && <div><label className="text-sm font-medium">Usuario *</label><Input value={username} onChange={(e) => setUsername(e.target.value)} className="mt-1" /></div>}
            <div><label className="text-sm font-medium">Email</label><Input value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" /></div>
            <div><label className="text-sm font-medium">{editing ? 'Nueva Contraseña (dejar vacío para mantener)' : 'Contraseña *'}</label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1" /></div>
            <div><label className="text-sm font-medium">Rol</label>
              <Select value={rol} onValueChange={setRol}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrador</SelectItem>
                  <SelectItem value="encargada">Encargada de Almacén</SelectItem>
                  <SelectItem value="supervisor">Supervisor</SelectItem>
                  <SelectItem value="consulta">Consulta</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#0F6E87] hover:bg-[#0C5D73]">{saving ? 'Guardando...' : 'Guardar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
